import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Typography, TextField, Button, Box, Alert, Checkbox, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid, Card,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { differenceInYears } from "date-fns";
import { RegisterPayload } from "../../types/models";
import { registerUser } from "../../services/User/service";
const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterPayload>({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    phone: "",
    date_of_birth: "",
  });
  const [retypePassword, setRetypePassword] = useState("");
  const [dob, setDob] = useState<Date | null>(null);
  const [isEulaChecked, setIsEulaChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEulaOpen, setIsEulaOpen] = useState(false);
  const [eulaText, setEulaText] = useState("");
  useEffect(() => {
    fetch("/eyeviation_tos.txt")
      .then((res) => res.text())
      .then((text) => setEulaText(text))
      .catch(() => setEulaText("Failed to load EULA."));
  }, []);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      setError("Invalid email address");
      return;
    }
    if (!dob) {
      setError("Please select your date of birth.");
      return;
    }
    const age = differenceInYears(new Date(), dob);
    if (age < 18) {
      setError("You must be at least 18 years old to register.");
      return;
    }
    if (formData.password !== retypePassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!isEulaChecked) {
      setError("You must agree to the EULA to register.");
      return;
    }
    try {
      await registerUser({
        ...formData,
        date_of_birth: dob.toISOString().split("T")[0],
      });
      navigate("/login");
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Registration failed");
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        className="auth-page"
        sx={{
          backgroundColor: "#0E1720",
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Card elevation={0}
          sx={{
            width: 600,
            padding: 4,
            backgroundColor: "#0E1720",
            borderRadius: 2,
          }}
        >
          <Box sx={{ textAlign: "center", marginBottom: 3 }}>
            <img
              id="vcat-logo"
              src="/logo.png"
              alt="VCAT Logo"
              style={{ width: "150px", marginBottom: "1rem" }}
            />
            <Typography
              id="register-heading"
              variant="h5"
              sx={{ color: "#FFFFFF", fontWeight: "bold" }}
            >
              Create Account
            </Typography>
          </Box>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 2 }}
          >
            <TextField
              id="username-input"
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              required
              sx={{
                "& .MuiInputLabel-root": { color: "#868B8F" },
                "& .MuiInputBase-input": { color: "#FFFFFF" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#868B8F" },
                  "&:hover fieldset": { borderColor: "#ffffff" },
                  "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                },
                "& .MuiInputLabel-asterisk": { display: "none" },
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  id="first-name-input"
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  sx={{
                    "& .MuiInputLabel-root": { color: "#868B8F" },
                    "& .MuiInputBase-input": { color: "#FFFFFF" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#868B8F" },
                      "&:hover fieldset": { borderColor: "#ffffff" },
                      "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                    },
                    "& .MuiInputLabel-asterisk": { display: "none" },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="last-name-input"
                  label="Last Name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  sx={{
                    "& .MuiInputLabel-root": { color: "#868B8F" },
                    "& .MuiInputBase-input": { color: "#FFFFFF" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#868B8F" },
                      "&:hover fieldset": { borderColor: "#ffffff" },
                      "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                    },
                    "& .MuiInputLabel-asterisk": { display: "none" },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="email-input"
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  sx={{
                    "& .MuiInputLabel-root": { color: "#868B8F" },
                    "& .MuiInputBase-input": { color: "#FFFFFF" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#868B8F" },
                      "&:hover fieldset": { borderColor: "#ffffff" },
                      "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                    },
                    "& .MuiInputLabel-asterisk": { display: "none" },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  id="phone-input"
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  sx={{
                    "& .MuiInputLabel-root": { color: "#868B8F" },
                    "& .MuiInputBase-input": { color: "#FFFFFF" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#868B8F" },
                      "&:hover fieldset": { borderColor: "#ffffff" },
                      "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                    },
                    "& .MuiInputLabel-asterisk": { display: "none" },
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              id="password-input"
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              required
              sx={{
                "& .MuiInputLabel-root": { color: "#868B8F" },
                "& .MuiInputBase-input": { color: "#FFFFFF" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#868B8F" },
                  "&:hover fieldset": { borderColor: "#ffffff" },
                  "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                },
                "& .MuiInputLabel-asterisk": { display: "none" },
              }}
            />
            <TextField
              id="retype-password-input"
              label="Retype Password"
              type="password"
              value={retypePassword}
              onChange={(e) => setRetypePassword(e.target.value)}
              variant="outlined"
              fullWidth
              required
              sx={{
                "& .MuiInputLabel-root": { color: "#868B8F" },
                "& .MuiInputBase-input": { color: "#FFFFFF" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#868B8F" },
                  "&:hover fieldset": { borderColor: "#ffffff" },
                  "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                },
                "& .MuiInputLabel-asterisk": { display: "none" },
              }}
            />
            <DatePicker
              label="Date of Birth"
              value={dob}
              onChange={(newValue) => setDob(newValue)}
              slotProps={{
                textField: {
                  id: "dob-input",
                  fullWidth: true,
                  variant: "outlined",
                  size: "small",
                  sx: {
                    "& .MuiInputLabel-asterisk": { display: "none" },
                    "& .MuiInputLabel-root": { color: "#868B8F" },
                    "& .MuiInputBase-input": { color: "#FFFFFF" },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#868B8F" },
                      "&:hover fieldset": { borderColor: "#ffffff" },
                      "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#FFFFFF",
                    },
                  },
                },
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  id="eula-checkbox"
                  checked={isEulaChecked}
                  onChange={(e) => setIsEulaChecked(e.target.checked)}
                  sx={{
                    color: "#1DB0BF",
                    '&.Mui-checked': {
                      color: "#1DB0BF"
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "#868B8F" }}>
                  I agree to the{" "}
                  <Link
                    to="#"
                    style={{ color: "#ffffff", textDecoration: "underline" }}
                    onClick={() => setIsEulaOpen(true)}
                  >
                    EULA
                  </Link>
                </Typography>
              }
            />
            {error && (
              <Alert
                id="error-message"
                severity="error"
                sx={{ backgroundColor: "#FF4B4B", color: "#FFFFFF" }}
              >
                {error}
              </Alert>
            )}
            <Button
              id="sign-up-button"
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                backgroundColor: "#1DB0BF",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "#1D6F78" },
                width: "100%",
                height: "50px",
                textAlign: "center",
                display: "block",
                margin: "0 auto",
                fontWeight: "bold",
              }}
            >
              Sign Up
            </Button>
            <Typography
              variant="body2"
              align="center"
              sx={{ color: "#fff", marginTop: 2 }}
            >
              Already have an account?{" "}
              <Link id="login-link" to="/login" style={{ textDecoration: "none", color: "#1DB0BF" }}>
                Sign in
              </Link>
            </Typography>
          </Box>
        </Card>
        <Dialog
          id="eula-popup"
          open={isEulaOpen}
          onClose={() => setIsEulaOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              backgroundColor: "#0E1720",
              color: "#FFFFFF",
            },
          }}
        >
          <DialogTitle>EULA - End User License Agreement</DialogTitle>
          <DialogContent dividers sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                color: '#FFFFFF',
              }}
            >
              {eulaText}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEulaOpen(false)} sx={{ color: "#1DB0BF" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </Box>

    </LocalizationProvider>
  );
};

export default Register;
